/**
 * A name-based UUID (RFC 4122 version 5), dependency-free and synchronous.
 *
 * Used so that two browser tabs rescheduling the SAME plan to the SAME date arrive at the
 * SAME new plan id: the API identifies a step by (plan, step) and answers a repeat with the
 * existing row, so the second tab's creates collapse onto the first's instead of making a
 * second plan. `crypto.subtle` is async and absent from some test runtimes, hence a small SHA-1.
 */

function sha1(bytes: Uint8Array): Uint8Array {
  const words: number[] = [];
  const bitLength = bytes.length * 8;
  const padded = new Uint8Array((((bytes.length + 8) >> 6) + 1) << 6);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 4, bitLength >>> 0);
  view.setUint32(padded.length - 8, Math.floor(bitLength / 2 ** 32));
  for (let i = 0; i < padded.length; i += 4) words.push(view.getUint32(i));

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;
  const rotl = (n: number, s: number) => ((n << s) | (n >>> (32 - s))) >>> 0;

  for (let block = 0; block < words.length; block += 16) {
    const w = words.slice(block, block + 16);
    for (let t = 16; t < 80; t += 1) w[t] = rotl(w[t - 3] ^ w[t - 8] ^ w[t - 14] ^ w[t - 16], 1);
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    for (let t = 0; t < 80; t += 1) {
      let f: number;
      let k: number;
      if (t < 20) { f = (b & c) | (~b & d); k = 0x5a827999; }
      else if (t < 40) { f = b ^ c ^ d; k = 0x6ed9eba1; }
      else if (t < 60) { f = (b & c) | (b & d) | (c & d); k = 0x8f1bbcdc; }
      else { f = b ^ c ^ d; k = 0xca62c1d6; }
      const temp = (rotl(a, 5) + (f >>> 0) + e + k + w[t]) >>> 0;
      e = d;
      d = c;
      c = rotl(b, 30);
      b = a;
      a = temp;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }
  const out = new Uint8Array(20);
  const outView = new DataView(out.buffer);
  [h0, h1, h2, h3, h4].forEach((h, i) => outView.setUint32(i * 4, h));
  return out;
}

const hexToBytes = (uuid: string): Uint8Array => {
  const hex = uuid.replace(/-/g, '');
  return Uint8Array.from({ length: 16 }, (_, i) => parseInt(hex.slice(i * 2, i * 2 + 2), 16));
};

/** UUIDv5 of `name` in `namespace` (a UUID string). */
export function uuidV5(name: string, namespace: string): string {
  const nameBytes = new TextEncoder().encode(name);
  const input = new Uint8Array(16 + nameBytes.length);
  input.set(hexToBytes(namespace));
  input.set(nameBytes, 16);
  const hash = sha1(input).slice(0, 16);
  hash[6] = (hash[6] & 0x0f) | 0x50;
  hash[8] = (hash[8] & 0x3f) | 0x80;
  const hex = Array.from(hash, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Fixed namespace for Studio's reminder-plan ids. */
export const REMINDER_PLAN_NAMESPACE = '6f1c2f3e-7a5b-5c1d-9e2a-3b4c5d6e7f80';

/**
 * The id of the plan that replaces `oldSequenceId` when it is rescheduled to `dateStart`.
 * Deterministic: every tab that reschedules that plan to that date computes the same id.
 */
export function rescheduledSequenceId(oldSequenceId: string, dateStart: string): string {
  return uuidV5(`${oldSequenceId}|${dateStart}`, REMINDER_PLAN_NAMESPACE);
}
